import { useTransportInfiniteQuery } from "@/transport"
import { keepPreviousData } from "@tanstack/react-query"

import type {
  TransportOperationRequest,
  TransportOperationResponse,
} from "@/types_api/backend/transport/operations"

type RunsKeysetRequest = TransportOperationRequest<"listRunsKeyset">
type RunSimulationsKeysetRequest =
  TransportOperationRequest<"listSimulatedConversationsKeyset">
type KeysetResponse = Pick<
  | TransportOperationResponse<"listSimulatedConversationsKeyset">
  | TransportOperationResponse<"listRunsKeyset">,
  "next_cursor"
>

const RUNS_KEYSET_STALE_TIME_MS = 5 * 60 * 1000

export function getNextKeysetRequest<
  Request extends RunsKeysetRequest | RunSimulationsKeysetRequest,
>(
  lastPage: KeysetResponse,
  previousRequest: Request | undefined,
): Request | null {
  if (!lastPage.next_cursor || !previousRequest) {
    return null
  }

  return {
    ...previousRequest,
    cursor: lastPage.next_cursor,
  }
}

export function useRunsKeysetTransport({
  enabled,
  request,
}: {
  enabled: boolean
  request: RunsKeysetRequest
}) {
  return useTransportInfiniteQuery({
    operation: "listRunsKeyset",
    request,
    getNextRequest: (lastPage, _allPages, previousRequest) =>
      getNextKeysetRequest(lastPage, previousRequest),
    options: {
      enabled,
      refetchOnWindowFocus: false,
      staleTime: RUNS_KEYSET_STALE_TIME_MS,
      placeholderData: keepPreviousData,
    },
  })
}

export function useRunSimulationsKeysetTransport({
  enabled,
  request,
}: {
  enabled: boolean
  request: RunSimulationsKeysetRequest
}) {
  return useTransportInfiniteQuery({
    operation: "listSimulatedConversationsKeyset",
    request,
    getNextRequest: (lastPage, _allPages, previousRequest) =>
      getNextKeysetRequest(lastPage, previousRequest),
    options: {
      enabled,
      refetchOnWindowFocus: false,
      staleTime: RUNS_KEYSET_STALE_TIME_MS,
      placeholderData: keepPreviousData,
    },
  })
}
